import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { switchMap, of } from 'rxjs';
import { PasswordValidationComponent } from '../password-validation/password-validation.component';
import { FormGroup, FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CheckboxComponent } from '../../components/checkbox/checkbox.component';
import { InputComponent } from '../../components/input/input.component';
import { showLoadingMaskAction, setUserDetailAction, setPermissionsAction } from '../../ngrx/actions/global.actions';
import { AlertService } from '../../services/alert.service';
import { AuthorizeService } from '../../services/authorize.service';
import { StorageService } from '../../services/storage.service';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, InputComponent, CheckboxComponent],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent extends PasswordValidationComponent {

  @Input() modalPadding?: boolean = false;
  @Output() loginSuccessEvent = new EventEmitter<any>();

  loginForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, this.passwordValidation.bind(this)]),
    rememberMe: new FormControl(true)
  });

  constructor(
    private authorizeService: AuthorizeService,
    private alertService: AlertService,
    private storageService: StorageService) {
    super();
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onSubmit();
    }
  }

  onSubmit() {
    this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));

    if (!this.loginForm.valid) {
      for (let control in this.loginForm.controls) {
        if (!this.loginForm.get(control)?.valid) {
          this.loginForm.get(control)?.markAsDirty();

          this.alertService.showSnackBar(`Error with ${control} input`);
          this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
          return;
        }
      }
    }

    let success = false;
    this.subs.push(this.authorizeService.login(
      this.loginForm.get('email')?.value,
      this.loginForm.get('password')?.value,
      this.loginForm.get('rememberMe')?.value
    ).pipe(
      switchMap((result) => {
        if (result.errors) {
          for (let error of result.errors) {
            this.alertService.showSnackBar(error);
          }
          return of(null);
        }
        else {
          this.storageService.setItem("accessToken", result.accessToken);
          this.storageService.setItem("accessTokenExpirey", result.accessTokenExpirey);

          // If they do not want to be remembered, set the refresh token
          // to the access token so that both will expire concurrently.
          if (this.loginForm.get('rememberMe')?.value === true) {
            this.storageService.setItem("refreshToken", result.refreshToken);
            this.storageService.setItem("refreshTokenExpirey", result.refreshTokenExpirey);
          } else {
            this.storageService.setItem("refreshToken", result.accessToken);
            this.storageService.setItem("refreshTokenExpirey", result.accessTokenExpirey);
          }

          this.storageService.setItem("user", JSON.stringify(result.user));
          this.authorizeService.setUserSubject(result.user);
          this.dispatch(setUserDetailAction({ userDetail: result.user }));
          success = true;
          return this.authorizeService.getPermissions();
        }
      })
    ).subscribe((permissions) => {
      if (permissions) {
        this.storageService.setItem("permissions", JSON.stringify(permissions));
        this.dispatch(setPermissionsAction({ permissions: permissions }));
      }
      if (success) {
        this.loginSuccessEvent.next(true);
      }
      this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
    }));
  }
}
